import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Mail, ChevronDown, ChevronUp, Check, Loader2, CircleCheck, CircleAlert, StickyNote, Plus, Pin, Archive, ArchiveRestore } from "lucide-react";
import { toast } from "sonner";
import { setStage, setTag, setEmail, addNote, setPin, setArchived } from "../lib/api";
import { setterName } from "../lib/leaderboard";
import { WinnerBadges } from "./WinnerBadges";
import { CallbackReminder } from "./CallbackReminder";
import { EmailHistory } from "./EmailHistory";
import { isCallbackDue } from "../lib/callback";

const PRIORITY = {
  hot: { label: "HOT", border: "#EF4444", text: "#F87171", bg: "rgba(239,68,68,0.15)" },
  warm: { label: "WARM", border: "#F59E0B", text: "#FBBF24", bg: "rgba(245,158,11,0.15)" },
  cold: { label: "COLD", border: "#3B82F6", text: "#60A5FA", bg: "rgba(59,130,246,0.15)" },
};

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "called", label: "Called" },
  { value: "closed", label: "Closed" },
  { value: "not_a_fit", label: "Not a fit" },
];

const STATUS_STYLE = {
  new: "bg-slate-800 text-slate-300 border-slate-600",
  applied: "bg-sky-950/60 text-sky-300 border-sky-600/50",
  called: "bg-amber-950/60 text-amber-300 border-amber-600/50",
  closed: "bg-purple-950/60 text-purple-300 border-purple-600/50",
  not_a_fit: "bg-red-950/50 text-red-400 border-red-800/40",
  approved: "bg-emerald-950/60 text-emerald-400 border-emerald-500/50",
  hired: "bg-amber-950/60 text-amber-300 border-amber-500/50",
};

export const LeadCard = ({ lead, onUpdated, winners = {} }) => {
  const [expanded, setExpanded] = useState(false);
  const [emailInput, setEmailInput] = useState(lead.email || "");
  const [emailBusy, setEmailBusy] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [emailMsg, setEmailMsg] = useState("");
  const [notes, setNotes] = useState(lead.call_notes || []);
  const [noteInput, setNoteInput] = useState("");
  const [noteBusy, setNoteBusy] = useState(false);
  const [pinBusy, setPinBusy] = useState(false);

  const p = PRIORITY[lead.priority] || PRIORITY.warm;

  const [archBusy, setArchBusy] = useState(false);
  const toggleArchive = async () => {
    setArchBusy(true);
    try {
      await setArchived(lead.id, !lead.archived);
      toast.success(lead.archived ? `${lead.full_name} restored` : `${lead.full_name} archived`);
      onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.toString() || "Could not update archive");
    } finally {
      setArchBusy(false);
    }
  };

  const togglePin = async () => {
    setPinBusy(true);
    try {
      await setPin(lead.id, !lead.pinned);
      onUpdated();
    } finally {
      setPinBusy(false);
    }
  };

  const saveNote = async () => {
    if (!noteInput.trim()) return;
    setNoteBusy(true);
    try {
      const { data } = await addNote(lead.id, noteInput.trim());
      setNotes((n) => [...n, data.note]);
      setNoteInput("");
    } finally {
      setNoteBusy(false);
    }
  };

  const fmtTime = (iso) => {
    try {
      return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    } catch {
      return "";
    }
  };

  const reportEmail = (res, sentMsg) => {
    if (!res) return;
    if (res.endsWith("_sent")) toast.success(sentMsg);
    else if (res.startsWith("email_failed")) toast.error(`Email not sent: ${res.replace("email_failed: ", "")}`);
  };

  const changeStatus = async (value, undo = false) => {
    setStatusBusy(true);
    try {
      const { data } = await setStage(lead.id, value, undo);
      reportEmail(data.email, value === "approved" ? `Approval email sent to ${lead.email}` : `Application email sent to ${lead.email}`);
      onUpdated();
    } finally {
      setStatusBusy(false);
    }
  };

  const tags = lead.tags || [];
  const toggleTag = async (tag) => {
    setStatusBusy(true);
    try {
      await setTag(lead.id, tag, !tags.includes(tag));
      onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.toString() || "Could not update");
    } finally {
      setStatusBusy(false);
    }
  };

  const isHired = lead.stage === "hired";
  const isApproved = lead.stage === "approved" || isHired;
  const isApplied = lead.stage === "applied" || isApproved;
  const toggleApplied = () => (lead.stage === "applied" ? changeStatus(null, true) : changeStatus("applied"));
  const toggleApproved = () => (isApproved ? changeStatus("applied", true) : changeStatus("approved"));
  const toggleHired = () => (isHired ? changeStatus("approved", true) : changeStatus("hired"));

  const saveEmail = async () => {
    if (!emailInput.trim()) return;
    setEmailBusy(true);
    setEmailMsg("");
    try {
      const { data } = await setEmail(lead.id, emailInput.trim());
      setEmailMsg("Saved");
      setTimeout(() => setEmailMsg(""), 2500);
      reportEmail(data.welcome, `Welcome email sent to ${data.email}`);
      onUpdated();
    } catch (e) {
      setEmailMsg("Invalid email");
    } finally {
      setEmailBusy(false);
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      data-testid={`lead-card-${lead.id}`}
      className={`relative bg-[#13161C] hover:bg-[#1A1E26] border rounded-md p-4 transition-all duration-150 ${lead.pinned ? "border-[#F95722] ring-1 ring-[#F95722]/40" : isCallbackDue(lead) ? "border-amber-400/70 ring-1 ring-amber-400/30" : lead.priority === "hot" ? "border-red-500/40 hot-glow" : "border-[#262C38] hover:border-[#384152]"}`}
    >
      {lead.pinned && (
        <div data-testid={`lead-pinned-flag-${lead.id}`} className="absolute -top-2 left-3 bg-[#F95722] text-white text-[9px] font-mono font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm inline-flex items-center gap-1">
          <Pin className="h-2.5 w-2.5" /> Pinned
        </div>
      )}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <span
            data-testid={`lead-priority-badge-${lead.id}`}
            className="font-mono text-xs font-bold tracking-wider px-2.5 py-1 uppercase rounded-sm border shrink-0"
            style={{ color: p.text, borderColor: p.border, background: p.bg }}
          >
            {p.label}
          </span>
          <div className="min-w-0">
            <h3 className="font-heading text-lg font-bold text-white truncate leading-tight">{lead.full_name}</h3>
            <p className="text-xs text-slate-400 truncate">{lead.reason}</p>
            <p data-testid={`lead-setter-${lead.id}`} className="font-mono text-[10px] uppercase tracking-wider text-slate-500 truncate">Set by {setterName(lead)}</p>
            <WinnerBadges lead={lead} winners={winners} />
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            data-testid={`lead-archive-btn-${lead.id}`}
            onClick={toggleArchive}
            disabled={archBusy}
            title={lead.archived ? "Restore to board" : "Archive (ghosted / unqualified)"}
            className="p-2.5 rounded border border-[#262C38] text-slate-400 hover:border-slate-400 hover:text-white transition-colors disabled:opacity-50"
          >
            {lead.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
          </button>
          <button
            data-testid={`lead-pin-btn-${lead.id}`}
            onClick={togglePin}
            disabled={pinBusy}
            title={lead.pinned ? "Unpin" : "Pin to top until called"}
            className={`p-2.5 rounded border transition-colors disabled:opacity-50 ${lead.pinned ? "bg-[#F95722] border-[#F95722] text-white" : "bg-transparent border-[#262C38] text-slate-400 hover:border-[#F95722] hover:text-[#F95722]"}`}
          >
            <Pin className={`h-4 w-4 ${lead.pinned ? "fill-current" : ""}`} />
          </button>
          {lead.phone && (
            <a
              data-testid={`lead-phone-dial-btn-${lead.id}`}
              href={`tel:${lead.phone.replace(/[^\d+]/g, "")}`}
              className="inline-flex items-center justify-center gap-2 bg-[#F95722] hover:bg-[#FF6B38] text-white font-bold py-2.5 px-4 rounded text-sm transition-transform active:scale-95 shadow-md"
            >
              <Phone className="h-4 w-4" />
              <span className="font-mono">{lead.phone}</span>
            </a>
          )}
        </div>
      </div>

      {/* Status pills */}
      <div className="flex items-center gap-1.5 mt-4 flex-wrap" data-testid={`lead-status-select-${lead.id}`}>
        {STATUS_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            disabled={statusBusy}
            data-testid={`lead-tag-${opt.value}-${lead.id}`}
            aria-pressed={tags.includes(opt.value)}
            onClick={() => toggleTag(opt.value)}
            className={`text-xs font-mono px-2.5 py-1 rounded border transition-colors disabled:opacity-60 ${tags.includes(opt.value) ? STATUS_STYLE[opt.value] : "bg-transparent text-slate-500 border-[#262C38] hover:border-[#384152]"}`}
          >
            {tags.includes(opt.value) && <Check className="inline h-3 w-3 mr-1" />}
            {opt.label}
          </button>
        ))}
      </div>

      {/* Email + approve row */}
      <div className="flex items-end gap-2 mt-4 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <label className="font-mono text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1 mb-1">
            <Mail className="h-3 w-3" /> Driver email
          </label>
          <div className="flex gap-2">
            <input
              data-testid={`lead-email-input-${lead.id}`}
              type="email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder="add driver email"
              className="flex-1 bg-[#0B0C0E] border border-[#262C38] rounded px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#F95722]"
            />
            <button
              data-testid={`lead-save-email-btn-${lead.id}`}
              onClick={saveEmail}
              disabled={emailBusy || !emailInput.trim()}
              className="inline-flex items-center gap-1 bg-[#181C24] border border-[#384152] hover:border-[#F95722] text-white px-3 py-2 rounded text-xs font-bold disabled:opacity-50"
            >
              {emailBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
            </button>
          </div>
          <div data-testid={`lead-auto-email-status-${lead.id}`} className="mt-1 text-[11px] font-mono flex flex-wrap gap-x-2">
            {lead.email && (lead.welcomed_emails || []).includes(lead.email.toLowerCase()) && (
              <span data-testid={`lead-welcome-sent-${lead.id}`} className="text-emerald-400 inline-flex items-center gap-1"><CircleCheck className="h-3 w-3" /> Welcome email sent</span>
            )}
            {lead.applied_email_sent && (
              <span className="text-emerald-400 inline-flex items-center gap-1"><CircleCheck className="h-3 w-3" /> Application email sent</span>
            )}
            {lead.approval_email_sent && (
              <span className="text-emerald-400 inline-flex items-center gap-1"><CircleCheck className="h-3 w-3" /> Approval email sent</span>
            )}
            {lead.email && !(lead.welcomed_emails || []).includes(lead.email.toLowerCase()) && (
              <span data-testid={`lead-email-unsent-hint-${lead.id}`} className="text-sky-300">Click Save to send the welcome email</span>
            )}
            {!lead.email && (
              <span className="text-slate-500 inline-flex items-center gap-1"><CircleAlert className="h-3 w-3" /> Add email to send welcome + status emails</span>
            )}
            {emailMsg && <span className="text-amber-300">{emailMsg}</span>}
          </div>
        </div>
        <div className="flex gap-2">
          <StageToggle testid={`applied-toggle-btn-${lead.id}`} checked={isApplied} disabled={statusBusy || isApproved}
            onClick={toggleApplied} label="Applied" tone="sky" />
          <StageToggle testid={`approve-driver-btn-${lead.id}`} checked={isApproved} disabled={statusBusy || isHired}
            onClick={toggleApproved} label="Approved" tone="emerald" />
          <StageToggle testid={`hired-toggle-btn-${lead.id}`} checked={isHired} disabled={statusBusy}
            onClick={toggleHired} label="Hired" tone="amber" />
        </div>
      </div>

      <CallbackReminder lead={lead} onUpdated={onUpdated} />
      <EmailHistory lead={lead} />

      {/* Slack note expander */}
      <button
        data-testid={`lead-slack-note-toggle-${lead.id}`}
        onClick={() => setExpanded((v) => !v)}
        className="mt-3 inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest text-slate-500 hover:text-slate-300 transition-colors"
      >
        {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        Position / info
      </button>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.pre
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden whitespace-pre-wrap text-xs text-slate-300 bg-[#0B0C0E] border border-[#262C38] rounded p-3 mt-2 font-mono"
          >
            {lead.notes || "No position / info in the Slack post."}
          </motion.pre>
        )}
      </AnimatePresence>

      {/* Call notes log */}
      <div className="mt-3 border-t border-[#262C38] pt-3" data-testid={`lead-notes-log-${lead.id}`}>
        <div className="flex items-center gap-1 mb-2">
          <StickyNote className="h-3 w-3 text-slate-500" />
          <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
            Call notes {notes.length > 0 ? `(${notes.length})` : ""}
          </span>
        </div>
        {notes.length > 0 && (
          <ul className="space-y-1.5 mb-2">
            {notes.map((n, i) => (
              <li key={i} className="text-xs text-slate-300 bg-[#0B0C0E] border border-[#262C38] rounded px-2.5 py-1.5">
                <span className="block">{n.text}</span>
                <span className="font-mono text-[10px] text-slate-600">{fmtTime(n.at)}</span>
              </li>
            ))}
          </ul>
        )}
        <div className="flex gap-2">
          <input
            data-testid={`lead-note-input-${lead.id}`}
            value={noteInput}
            onChange={(e) => setNoteInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") saveNote(); }}
            placeholder="Log what happened on the call..."
            className="flex-1 bg-[#0B0C0E] border border-[#262C38] rounded px-3 py-2 text-xs text-white placeholder:text-slate-600 focus:outline-none focus:border-[#F95722]"
          />
          <button
            data-testid={`lead-add-note-btn-${lead.id}`}
            onClick={saveNote}
            disabled={noteBusy || !noteInput.trim()}
            className="inline-flex items-center gap-1 bg-[#181C24] border border-[#384152] hover:border-[#F95722] text-white px-3 py-2 rounded text-xs font-bold disabled:opacity-50"
          >
            {noteBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const TONES = {
  sky: "bg-sky-600 border-sky-500 text-white",
  emerald: "bg-emerald-600 border-emerald-500 text-white",
  amber: "bg-amber-500 border-amber-400 text-black",
};

const StageToggle = ({ testid, checked, disabled, onClick, label, tone }) => (
  <button
    data-testid={testid}
    aria-pressed={checked}
    onClick={onClick}
    disabled={disabled}
    title={checked ? `Untick ${label}` : `Mark ${label}`}
    className={`inline-flex items-center gap-1.5 border font-bold py-2 px-3 rounded text-xs uppercase tracking-wide transition-transform active:scale-95 disabled:opacity-50 ${checked ? TONES[tone] : "bg-transparent border-[#384152] text-slate-300 hover:border-slate-400"}`}
  >
    <span className={`h-3.5 w-3.5 rounded-sm border flex items-center justify-center ${checked ? "border-white bg-white/20" : "border-slate-500"}`}>
      {checked && <Check className="h-3 w-3" />}
    </span>
    {label}
  </button>
);
